/**
 * Admin → Affiliates tab.
 *
 * Ported from `routes/admin.ts`. Lists affiliates with status + earnings; an
 * inline Create panel and an inline Edit panel handle CRUD against
 * /admin/affiliates and /admin/affiliates/:id.
 */
import { useEffect, useState } from 'hono/jsx';
import { AdminShell } from '../../components/AdminShell';
import { api } from '../../lib/api';
import { showToast } from '../../lib/toast';
import { Spinner } from '../../components/Spinner';

interface Affiliate {
  id: string;
  name: string;
  email: string;
  code: string;
  isActive: boolean;
  commission: { setupPct: number | null; recurringPct: number | null };
  stripe: { onboardingComplete: boolean };
  earnings: { totalEarned: number };
  referralCount: number;
}

export function AdminAffiliates() {
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState<boolean>(false);
  const [editing, setEditing] = useState<Affiliate | null>(null);

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api('/admin/affiliates');
      if (!r.ok) throw new Error('Failed to load affiliates');
      const data = await r.json() as { affiliates?: Affiliate[] };
      setAffiliates(data.affiliates || []);
    } catch (e: any) {
      setLoadError(e?.message || 'Failed to load affiliates');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function openEdit(a: Affiliate) {
    setShowCreate(false);
    setEditing(a);
  }

  function openCreate() {
    setEditing(null);
    setShowCreate((v) => !v);
  }

  return (
    <AdminShell active="affiliates">
      <div class="flex flex-wrap gap-3 justify-between items-center mb-6">
        <h2 class="text-xl font-semibold text-slate-100">Affiliates</h2>
        <button
          type="button"
          onClick={openCreate}
          class="bg-cyan-500 text-dark-900 px-4 py-2 rounded-lg text-sm font-medium hover:bg-cyan-400 transition-colors"
        >+ Add Affiliate</button>
      </div>

      {showCreate && (
        <CreatePanel
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}

      {editing && (
        <EditPanel
          affiliate={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <div class="bg-dark-800 rounded-xl border border-slate-700/50 overflow-x-auto">
        {loading ? (
          <div class="p-8 text-center"><Spinner className="mx-auto" /><p class="text-slate-400 mt-4 text-sm">Loading affiliates...</p></div>
        ) : loadError ? (
          <div class="p-8 text-center text-red-400">{loadError}</div>
        ) : !affiliates.length ? (
          <div class="p-8 text-center text-slate-400">No affiliates yet</div>
        ) : (
          <AffiliatesTable affiliates={affiliates} onEdit={openEdit} />
        )}
      </div>
    </AdminShell>
  );
}

function AffiliatesTable({ affiliates, onEdit }: { affiliates: Affiliate[]; onEdit: (a: Affiliate) => void }) {
  return (
    <table class="w-full">
      <thead class="bg-dark-700">
        <tr>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Name</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Code</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Commission</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Earnings</th>
          <th class="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Actions</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-slate-700/50">
        {affiliates.map((a) => {
          const statusCls = a.stripe.onboardingComplete
            ? 'bg-emerald-500/20 text-emerald-400'
            : a.isActive
              ? 'bg-amber-500/20 text-amber-400'
              : 'bg-red-500/20 text-red-400';
          const statusLabel = a.stripe.onboardingComplete
            ? 'Active'
            : a.isActive ? 'Pending Stripe' : 'Inactive';
          return (
            <tr class="hover:bg-dark-700/50">
              <td class="px-4 py-3">
                <div class="text-sm text-slate-200">{a.name}</div>
                <div class="text-xs text-slate-500">{a.email}</div>
              </td>
              <td class="px-4 py-3">
                <code class="text-sm text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded">{a.code}</code>
              </td>
              <td class="px-4 py-3 text-xs text-slate-400">
                {a.commission.setupPct !== null ? `${a.commission.setupPct}% setup` : 'Plan default'}
                {a.commission.recurringPct !== null ? ` / ${a.commission.recurringPct}% recurring` : ''}
              </td>
              <td class="px-4 py-3">
                <span class={`text-xs px-2 py-1 rounded ${statusCls}`}>{statusLabel}</span>
              </td>
              <td class="px-4 py-3">
                <div class="text-sm text-emerald-400">${a.earnings.totalEarned.toFixed(2)}</div>
                <div class="text-xs text-slate-500">{a.referralCount} referrals</div>
              </td>
              <td class="px-4 py-3">
                <button
                  type="button"
                  onClick={() => onEdit(a)}
                  class="text-cyan-400 hover:text-cyan-300 text-sm"
                >Edit</button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function CreatePanel({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [busy, setBusy] = useState<boolean>(false);

  async function onSubmit(e: Event) {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const fd = new FormData(form);
    const setup = fd.get('commissionSetupPct') as string;
    const recurring = fd.get('commissionRecurringPct') as string;
    const data: Record<string, unknown> = {
      name: fd.get('name'),
      email: fd.get('email'),
      code: (fd.get('code') as string) || undefined,
      commissionSetupPct: setup ? parseInt(setup, 10) : undefined,
      commissionRecurringPct: recurring ? parseInt(recurring, 10) : undefined,
    };

    setBusy(true);
    try {
      const r = await api('/admin/affiliates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await r.json() as { error?: string; affiliate?: { code: string } };
      if (!r.ok) throw new Error(result.error || 'Failed to create affiliate');
      showToast('success', 'Affiliate Created', 'Code: ' + (result.affiliate?.code || ''));
      onCreated();
    } catch (err: any) {
      showToast('error', 'Error', err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="mb-6 bg-dark-800 rounded-xl border border-cyan-500/30 shadow-xl">
      <div class="px-6 py-4 border-b border-slate-700/50 flex justify-between items-center">
        <h3 class="text-lg font-semibold text-slate-100">Add Affiliate</h3>
        <button type="button" onClick={onClose} class="text-slate-400 hover:text-slate-200" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
      <form onSubmit={onSubmit} class="p-6 space-y-4">
        <div class="grid sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">Name</label>
            <input type="text" name="name" required class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">Email</label>
            <input type="email" name="email" required class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-1">Referral Code (optional)</label>
          <input type="text" name="code" placeholder="Auto-generated if empty" class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
        </div>
        <div class="pt-2 border-t border-slate-700/50">
          <p class="text-xs text-slate-500 mb-3">Commission Rates (leave empty for plan defaults)</p>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Setup Fee %</label>
              <input type="number" step="1" min="0" max="100" name="commissionSetupPct" placeholder="e.g. 25" class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Recurring %</label>
              <input type="number" step="1" min="0" max="100" name="commissionRecurringPct" placeholder="e.g. 50" class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
          </div>
        </div>
        <button type="submit" disabled={busy} class="w-full bg-cyan-500 text-dark-900 py-2 rounded-lg font-medium hover:bg-cyan-400 transition-colors disabled:opacity-60">
          {busy ? 'Creating…' : 'Create Affiliate'}
        </button>
      </form>
    </div>
  );
}

function EditPanel({ affiliate, onClose, onSaved }: { affiliate: Affiliate; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState<string>(affiliate.name);
  const [email, setEmail] = useState<string>(affiliate.email);
  const [code, setCode] = useState<string>(affiliate.code);
  const [setupPct, setSetupPct] = useState<string>(affiliate.commission.setupPct == null ? '' : String(affiliate.commission.setupPct));
  const [recurringPct, setRecurringPct] = useState<string>(affiliate.commission.recurringPct == null ? '' : String(affiliate.commission.recurringPct));
  const [isActive, setIsActive] = useState<boolean>(affiliate.isActive);
  const [busy, setBusy] = useState<boolean>(false);

  async function onSubmit(e: Event) {
    e.preventDefault();
    setBusy(true);
    const data = {
      name,
      email,
      code,
      commissionSetupPct: setupPct !== '' ? parseInt(setupPct, 10) : null,
      commissionRecurringPct: recurringPct !== '' ? parseInt(recurringPct, 10) : null,
      isActive,
    };
    try {
      const r = await api('/admin/affiliates/' + affiliate.id, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!r.ok) {
        const result = await r.json().catch(() => ({})) as { error?: string };
        throw new Error(result.error || 'Failed to update affiliate');
      }
      showToast('success', 'Affiliate Updated', 'Changes saved successfully');
      onSaved();
    } catch (err: any) {
      showToast('error', 'Error', err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="mb-6 bg-dark-800 rounded-xl border border-cyan-500/30 shadow-xl">
      <div class="px-6 py-4 border-b border-slate-700/50 flex justify-between items-center">
        <h3 class="text-lg font-semibold text-slate-100">Edit Affiliate</h3>
        <button type="button" onClick={onClose} class="text-slate-400 hover:text-slate-200" aria-label="Close">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
      <form onSubmit={onSubmit} class="p-6 space-y-4">
        <div class="grid sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">Name</label>
            <input type="text" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} required class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-1">Email</label>
            <input type="email" value={email} onInput={(e) => setEmail((e.target as HTMLInputElement).value)} required class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-slate-300 mb-1">Referral Code</label>
          <input type="text" value={code} onInput={(e) => setCode((e.target as HTMLInputElement).value)} class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
        </div>
        <div class="pt-2 border-t border-slate-700/50">
          <p class="text-xs text-slate-500 mb-3">Commission Rates (leave empty for plan defaults)</p>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Setup Fee %</label>
              <input type="number" step="1" min="0" max="100" value={setupPct} onInput={(e) => setSetupPct((e.target as HTMLInputElement).value)} placeholder="Plan default" class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-1">Recurring %</label>
              <input type="number" step="1" min="0" max="100" value={recurringPct} onInput={(e) => setRecurringPct((e.target as HTMLInputElement).value)} placeholder="Plan default" class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50" />
            </div>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive((e.target as HTMLInputElement).checked)} id="editAffiliateActive" class="rounded border-slate-600/50 bg-dark-700 text-cyan-500 focus:ring-cyan-500/50" />
          <label for="editAffiliateActive" class="text-sm text-slate-300">Active</label>
        </div>
        <button type="submit" disabled={busy} class="w-full bg-cyan-500 text-dark-900 py-2 rounded-lg font-medium hover:bg-cyan-400 transition-colors disabled:opacity-60">
          {busy ? 'Saving…' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
}

export default AdminAffiliates;
